import {
  ANOSCOPY_GENIUS_DEVICES,
  EU_DEEP_CAPSULE_DEVICES,
  EU_DEVICES,
  EU_IBD_SMART_DEVICES,
  IDevice,
  SMART_CHOLANGIO_DEVICES,
  SMART_CYST_ULTRASOUND_DEVICES,
  SMART_PANCREAS_ULTRASOUND_DEVICES,
  US_DEEP_CAPSULE_DEVICES,
  US_DEVICES,
  US_IBD_SMART_DEVICES,
} from 'constants/devices';
import {
  ANOSCOPY_GENIUS_MODELS,
  DEEP_CAPSULE_MODELS,
  IBD_SMART_MODELS,
  SMART_CHOLANGIO_MODELS,
  SMART_CYST_MODELS,
  SMART_PANCREAS_MODELS,
  TECHNOLOGIES,
} from 'constants/products';
import { REGION_VARIANTS } from 'constants/region';
import { ExamDocument } from 'types/mongoose-types';

type TExamTechnologyLabel =
  | 'Deep Bowel'
  | 'Deep Capsule'
  | 'GI Spy'
  | 'IBD Smart'
  | 'Smart Cholangio'
  | 'Smart Cyst'
  | 'Smart Pancreas'
  | 'Anoscopy Genius';

export interface IProduct {
  id: ExamDocument['technology'];
  key: string;
  img: string;
  icon: string;
  label: TExamTechnologyLabel;
  description: string;
  furtherDescription?: string;
  devices: { id: string; label: string }[];
  models?: string[];
  hidden?: boolean;
  disabled?: boolean;
}

export const getSortedProductDevices = (
  deviceDictionary: IDevice[],
  deviceList: string[],
) =>
  deviceDictionary
    .filter(({ id }) => deviceList.includes(id))
    .sort((a, b) => deviceList.indexOf(a.id) - deviceList.indexOf(b.id));

export const TECHNOLOGY_PROPERTIES: Record<string, IProduct> = {
  [`${TECHNOLOGIES.deepCapsule}_${REGION_VARIANTS.EU}`]: {
    id: TECHNOLOGIES.deepCapsule,
    key: `${TECHNOLOGIES.deepCapsule}_EU`,
    img: `/static/logos/deep-capsule.svg`,
    icon: `/static/logos/deep-capsule-icon.svg`,
    label: 'Deep Capsule',
    description:
      'Detect and differentiate endoscopic lesions with groundbreaking sensitivity and specificity.',
    devices: getSortedProductDevices(EU_DEVICES, EU_DEEP_CAPSULE_DEVICES),
    models: Object.values(DEEP_CAPSULE_MODELS),
  },
  [`${TECHNOLOGIES.deepCapsule}_${REGION_VARIANTS.US}`]: {
    id: TECHNOLOGIES.deepCapsule,
    key: `${TECHNOLOGIES.deepCapsule}_US`,
    img: `/static/logos/deep-capsule.svg`,
    icon: `/static/logos/deep-capsule-icon.svg`,
    label: 'Deep Capsule',
    description:
      'Detect endoscopic lesions with groundbreaking sensitivity and specificity.',
    devices: getSortedProductDevices(US_DEVICES, US_DEEP_CAPSULE_DEVICES),
    models: Object.values(DEEP_CAPSULE_MODELS),
  },
  [TECHNOLOGIES.smartCholangio]: {
    id: TECHNOLOGIES.smartCholangio,
    key: TECHNOLOGIES.smartCholangio,
    img: `/static/logos/smart-cholangio.svg`,
    icon: `/static/logos/smart-cholangio-icon.svg`,
    label: 'Smart Cholangio',
    description:
      'Optimized detection and characterization of biliary strictures.',
    furtherDescription: 'Accurate cholangiocarcinoma detection.',
    devices: getSortedProductDevices(EU_DEVICES, SMART_CHOLANGIO_DEVICES),
    models: Object.values(SMART_CHOLANGIO_MODELS),
  },
  [TECHNOLOGIES.smartCyst]: {
    id: TECHNOLOGIES.smartCyst,
    key: TECHNOLOGIES.smartCyst,
    img: `/static/logos/smart-cyst.svg`,
    icon: `/static/logos/smart-cyst-vertical.svg`,
    label: 'Smart Cyst',
    description:
      'Detect and characterize cysts and polyps in the gastrointestinal tract.',
    devices: getSortedProductDevices(EU_DEVICES, SMART_CYST_ULTRASOUND_DEVICES),
    models: Object.values(SMART_CYST_MODELS),
  },
  [TECHNOLOGIES.smartPancreas]: {
    id: TECHNOLOGIES.smartPancreas,
    key: TECHNOLOGIES.smartPancreas,
    img: `/static/logos/smart-pancreas.svg`,
    icon: `/static/logos/smart-pancreas-vertical.svg`,
    label: 'Smart Pancreas',
    description:
      'Advanced detection and characterization of pancreatic cysts and tumors.',
    devices: getSortedProductDevices(
      EU_DEVICES,
      SMART_PANCREAS_ULTRASOUND_DEVICES,
    ),
    models: Object.values(SMART_PANCREAS_MODELS),
  },
  [TECHNOLOGIES.anoscopyGenius]: {
    id: TECHNOLOGIES.anoscopyGenius,
    key: TECHNOLOGIES.anoscopyGenius,
    img: `/static/logos/anoscopy-genius.svg`,
    icon: `/static/logos/anoscopy-genius-vertical.svg`,
    label: 'Anoscopy Genius',
    description:
      'Advanced detection and characterization of anorectal lesions.',
    devices: getSortedProductDevices(EU_DEVICES, ANOSCOPY_GENIUS_DEVICES),
    models: Object.values(ANOSCOPY_GENIUS_MODELS),
  },
  [`${TECHNOLOGIES.ibdSmart}_${REGION_VARIANTS.EU}`]: {
    id: TECHNOLOGIES.ibdSmart,
    key: `${TECHNOLOGIES.ibdSmart}_EU`,
    img: `/static/logos/ibd-smart.svg`,
    icon: `/static/logos/ibd-smart-icon.svg`,
    label: 'IBD Smart',
    description:
      'Identify ulcers and erosions, in the small bowel and colon, in images derived from capsule endoscopy videos.',
    devices: getSortedProductDevices(EU_DEVICES, EU_IBD_SMART_DEVICES),
    models: Object.values(IBD_SMART_MODELS),
  },
  [`${TECHNOLOGIES.ibdSmart}_${REGION_VARIANTS.US}`]: {
    id: TECHNOLOGIES.ibdSmart,
    key: `${TECHNOLOGIES.ibdSmart}_US`,
    img: `/static/logos/ibd-smart.svg`,
    icon: `/static/logos/ibd-smart-icon.svg`,
    label: 'IBD Smart',
    description:
      'Identify ulcers and erosions, in the small bowel and colon, in images derived from capsule endoscopy videos.',
    devices: getSortedProductDevices(US_DEVICES, US_IBD_SMART_DEVICES),
    models: Object.values(IBD_SMART_MODELS),
  },
};
